// Cloudflare Turnstile (anti-robot) — utilisé par l'intercepteur de lib/api.js.
//
// Aucun formulaire n'a besoin d'être modifié : avant chaque POST vers un endpoint
// public protégé, on obtient un jeton Turnstile et on l'envoie dans l'en-tête
// X-Turnstile-Token. Le widget est en mode « interaction-only » : invisible pour
// la grande majorité des visiteurs ; si Cloudflare demande un clic, il s'affiche
// dans une popup centrée « Une dernière vérification ».
//
// Côté serveur : backend/turnstile_guard.py (clé secrète TURNSTILE_SECRET_KEY).

const SITE_KEY =
  process.env.REACT_APP_TURNSTILE_SITE_KEY || "0x4AAAAAAFNB4mWwGAWgIthJ";

const PROTECTED = [
  /^\/?contact-messages\/?$/,
  /^\/?callback-requests\/?$/,
  /^\/?quote-requests\/?$/,
  /^\/?isolation-pac\/leads\/?$/,
];

export function needsTurnstile(config) {
  if (!config || (config.method || "get").toLowerCase() !== "post") return false;
  const url = (config.url || "").split("?")[0].replace(/^.*\/api/, "");
  return PROTECTED.some((re) => re.test(url));
}

let scriptPromise = null;

function loadScript() {
  if (window.turnstile) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => {
        scriptPromise = null;
        reject(new Error("turnstile: script non chargé"));
      };
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
}

// Fenêtre de vérification : invisible tant que Cloudflare valide en silence ;
// devient une popup centrée (fond assombri) si un clic est demandé.
function createOverlay() {
  const overlay = document.createElement("div");
  overlay.setAttribute("data-em-turnstile", "");
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-label", "Vérification anti-robot");
  Object.assign(overlay.style, {
    position: "fixed",
    inset: "0",
    zIndex: "2147483647",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px",
    background: "rgba(10, 31, 28, 0.55)",
    backdropFilter: "blur(4px)",
    WebkitBackdropFilter: "blur(4px)",
    opacity: "0",
    pointerEvents: "none",
    transition: "opacity .2s ease",
  });

  const card = document.createElement("div");
  Object.assign(card.style, {
    width: "100%",
    maxWidth: "380px",
    background: "#ffffff",
    borderRadius: "24px",
    padding: "28px 24px 24px",
    boxShadow: "0 24px 60px rgba(0,0,0,.25)",
    textAlign: "center",
    fontFamily: "inherit",
    color: "#153F3A",
    transform: "translateY(8px)",
    transition: "transform .2s ease",
  });

  const badge = document.createElement("div");
  Object.assign(badge.style, {
    width: "56px",
    height: "56px",
    margin: "0 auto 16px",
    borderRadius: "50%",
    background: "#C7FF2F",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  });
  badge.innerHTML =
    '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#153F3A" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3l7 3v5c0 4.5-3 8.3-7 10-4-1.7-7-5.5-7-10V6l7-3z"/><path d="M9 12l2 2 4-4"/></svg>';

  const title = document.createElement("div");
  title.textContent = "Une dernière vérification";
  Object.assign(title.style, { fontSize: "20px", fontWeight: "700", marginBottom: "8px" });

  const text = document.createElement("div");
  text.textContent =
    "Pour envoyer votre demande, cochez la case ci-dessous. Cela nous protège des robots.";
  Object.assign(text.style, { fontSize: "15px", lineHeight: "1.45", color: "#3d5a56", marginBottom: "20px" });

  const slot = document.createElement("div");
  Object.assign(slot.style, { display: "flex", justifyContent: "center", minHeight: "65px" });

  card.append(badge, title, text, slot);
  overlay.appendChild(card);
  document.body.appendChild(overlay);

  return {
    overlay,
    slot,
    show() {
      overlay.style.opacity = "1";
      overlay.style.pointerEvents = "auto";
      card.style.transform = "translateY(0)";
    },
    hide() {
      overlay.style.opacity = "0";
      overlay.style.pointerEvents = "none";
    },
  };
}

export async function getTurnstileToken() {
  await loadScript();
  return new Promise((resolve, reject) => {
    const ui = createOverlay();
    const box = ui.slot;

    let widgetId = null;
    let settled = false;
    const finish = (fn, value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try {
        if (widgetId !== null) window.turnstile.remove(widgetId);
      } catch (_) {
        /* ignore */
      }
      ui.overlay.remove();
      fn(value);
    };
    const timer = setTimeout(
      () => finish(reject, new Error("turnstile: délai dépassé")),
      90000
    );

    try {
      widgetId = window.turnstile.render(box, {
        sitekey: SITE_KEY,
        appearance: "interaction-only",
        language: "fr",
        "before-interactive-callback": () => ui.show(),
        "after-interactive-callback": () => ui.hide(),
        callback: (token) => finish(resolve, token),
        "error-callback": () => finish(reject, new Error("turnstile: erreur")),
      });
    } catch (e) {
      finish(reject, e);
    }
  });
}
